import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Flex, Heading, Image, useColorModeValue, useToast, VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

// The OAuth callback runs on the backend, so a failure can only come back as a
// redirect. `?auth=` is how it says what went wrong.
const AUTH_ERROR_KEYS = {
  error: 'login.errorGeneric',
  unverified: 'login.errorUnverified',
  disabled: 'login.errorDisabled',
};

const LoginPage = () => {
  const { login } = useAuth();
  const { t } = useTranslation('auth');
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // The squircle app icon rather than the bare mark: it reads as a product
  // badge at this size, and the two variants already invert correctly —
  // light mode gets the ink tile, dark mode the cream one.
  const iconSrc = useColorModeValue('/brand/app-icon-light.svg', '/brand/app-icon-dark.svg');

  useEffect(() => {
    const reason = searchParams.get('auth');
    if (!reason) return;
    toast({
      title: t('login.errorTitle'),
      description: t(AUTH_ERROR_KEYS[reason] || 'login.errorGeneric'),
      status: 'error',
      duration: 6000,
      isClosable: true,
    });
    // Clear it so a reload doesn't re-raise a stale failure.
    searchParams.delete('auth');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, toast, t]);

  return (
    <Flex minH="100vh" align="center" justify="center" bg="redesign.bg.page" px={4}>
      <Flex
        bg="redesign.bg.surface"
        borderWidth="1px"
        borderColor="redesign.border"
        borderRadius="2xl"
        boxShadow="card"
        w="full"
        maxW="440px"
        minH={{ base: 'auto', md: '520px' }}
        px={{ base: 8, md: 12 }}
        py={{ base: 12, md: 16 }}
        direction="column"
        align="center"
        justify="center"
        // Sits the content a touch above the card's centre. Dead-centre reads
        // as bottom-heavy once the eye accounts for the title above the button.
        pb={{ base: 12, md: 32 }}
      >
        <VStack spacing={6} w="full">
          <Image src={iconSrc} alt="Mi Kapital" boxSize="56px" borderRadius="14px" />
          <Heading
            as="h1"
            size="md"
            fontWeight="semibold"
            letterSpacing="-0.02em"
            color="redesign.heading"
            textAlign="center"
          >
            {t('login.title')}
          </Heading>
          <Box w="full" pt={4}>
            <Button
              w="full"
              size="lg"
              variant="primary"
              fontWeight="medium"
              borderRadius="lg"
              onClick={() => login('/')}
            >
              {t('login.continueWithGoogle')}
            </Button>
          </Box>
        </VStack>
      </Flex>
    </Flex>
  );
};

export default LoginPage;
